import api from '@/lib/api/axios'

export interface IaCProject {
    id: number
    mainId: string
    name: string
    description: string | null
    cloudPlatform: string
    status: string
    summary: string | null
    createdAt: string
    updatedAt: string
    files?: IaCFile[]
}

export interface IaCFile {
    id: number
    projectId: number
    filePath: string
    content: string
    description: string | null
    version: number
    createdAt: string
    updatedAt: string
}

export interface IaCContextObject {
    objectId: string
    description: string | null
    layerId: number | null
    layerDescription: string | null
    isDatastore: boolean
    technologyDescription: string | null
    technologyModel: string | null
    technologyCategory: string | null
    objectService: string | null
    locationName: string | null
    versions: Array<{
        versionId: number
        type: string | null
        chainSet: string | null
        analytics: number | null
        size: number | null
        anomaly: number | null
        primaryChainCompromised: boolean
        vaultDatetime: string | null
        metadata: Record<string, any> | null
    }>
}

export interface IaCContext {
    mainId: string
    scenario: number
    serviceName: string
    objectCount: number
    versionCount: number
    hierarchyData: IaCContextObject[]
    dependencies: Array<{ source: string; target: string }>
}

export const iacApi = {
    // Get hierarchy context for IaC generation
    getContext: (mainId: string, scenario?: number) =>
        api.get<{ success: boolean; data: IaCContext }>(`/api/objects/${encodeURIComponent(mainId)}/iac-context`, {
            params: { scenario: scenario || 1 },
        }),

    // IaC list screen — one consolidated query (vw_IaCList view), one row per session
    list: () =>
        api.get<{ success: boolean; sessions: any[] }>('/api/ai/iac/list'),

    // Chat with IaC agent
    chat: (message: string, sessionId?: string, context?: any) =>
        api.post('/api/ai/iac/chat', { message, sessionId, context }),

    // Derive the architecture diagram ({containers, nodes, edges}) from the current Terraform.
    // This runs the model over all the .tf files; how long depends on the environment's
    // complexity, so there's no fixed timeout (timeout: 0) — consistent with the other AI
    // calls, which stream over fetch with no timeout. Result is cached server-side after.
    deriveArchitecture: (files: Array<{ filename: string; code: string }>) =>
        api.post<{ success: boolean; data: any; cached?: boolean }>('/api/ai/iac/architecture', { files }, { timeout: 0 }),

    // Run `terraform validate` server-side and auto-fix errors via the model,
    // returning the (possibly corrected) files + the final validation result.
    // No timeout: terraform init can download providers on a cold cache.
    validate: (files: Array<{ filename: string; code: string }>, maxAttempts = 3) =>
        api.post<{
            success: boolean; ran: boolean; valid: boolean; attempts: number;
            errorCount: number; warningCount: number;
            diagnostics: Array<{ severity: string; summary: string; detail: string; file: string | null; line: number | null }>;
            files: Array<{ filename: string; code: string }>; changedFiles: string[]; message?: string;
        }>('/api/ai/iac/validate', { files, maxAttempts }, { timeout: 0 }),

    // Projects
    listProjects: (mainId?: string) =>
        api.get('/api/ai/iac/projects', { params: mainId ? { mainId } : {} }),

    getProject: (id: number) =>
        api.get(`/api/ai/iac/projects/${id}`),

    createProject: (data: { mainId: string; name: string; description?: string; cloudPlatform: string; scenario?: number; summary?: string; userId?: number }) =>
        api.post('/api/ai/iac/projects', data),

    deleteProject: (id: number) =>
        api.delete(`/api/ai/iac/projects/${id}`),

    // Files
    saveFile: (projectId: number, data: { filePath: string; content: string; description?: string }) =>
        api.post(`/api/ai/iac/projects/${projectId}/files`, data),

    updateFile: (fileId: number, data: { content?: string; description?: string }) =>
        api.put(`/api/ai/iac/files/${fileId}`, data),

    deleteFile: (fileId: number) =>
        api.delete(`/api/ai/iac/files/${fileId}`),

    // Credentials
    checkCredentials: (cloudProvider: string) =>
        api.get(`/api/iac/credentials/${cloudProvider}`),

    saveCredentials: (cloudProvider: string, data: Record<string, string>) =>
        api.put(`/api/iac/credentials/${cloudProvider}`, data),

    deleteCredentials: (cloudProvider: string) =>
        api.delete(`/api/iac/credentials/${cloudProvider}`),

    // Deployments
    getDeployment: (projectId: number) =>
        api.get(`/api/iac/deployments/project/${projectId}`),

    createDeployment: (data: { projectId: number; cloudProvider: string; region?: string }) =>
        api.post('/api/iac/deployments', data),

    updateDeploymentStatus: (deploymentId: number, data: { status: string; statusMessage?: string; planOutput?: string; applyOutput?: string; destroyOutput?: string; outputs?: any }) =>
        api.put(`/api/iac/deployments/${deploymentId}/status`, data),
}

export interface IaCDeployment {
    id: number
    projectId: number
    cloudProvider: string
    region: string | null
    status: 'not_deployed' | 'pending' | 'planning' | 'planned' | 'deploying' | 'deployed' | 'destroying' | 'destroyed' | 'failed'
    statusMessage: string | null
    planOutput: string | null
    applyOutput: string | null
    destroyOutput: string | null
    deployedAt: string | null
    destroyedAt: string | null
    createdAt: string
    deployToken?: string
}

export interface IaCCredentialInfo {
    exists: boolean
    cloudProvider: string
    configured?: boolean
    accessKeyHint?: string
    region?: string | null
}
